var Component = require("enyo/Component"),
    kind      = require("enyo/kind");

/**
 * This object is meant to expose some useful application state globally.
 */
module.exports = kind(/** @lends app.AppState.AppState.prototype */ {

    /**
     * @private
     */
    name: "app.AppState.AppState",

    /**
     * @private
     */
    kind: Component,

    /**
     * @method
     * @private
     */
    constructor: kind.inherit(function(sup) {
        return function() {
            this.pagePlaylistSlugs = [];
            sup.apply(this, arguments);
        };
    }),

    // Preview mode State
    // --------------------------------

    previewMode: false,

    // Page State
    // --------------------------------

    /**
     * Reset page state
     *
     * @public
     */
    resetPageState: function() {
        this.set("pageController", null);
        this.set("pageActive", -1);
        this.set("pageMediaType", null);
        this.set("pageMediaSlug", null);
        this.set("pageMediaId", null);
        this.set("pagePlaylistSlugs", []);
    },

    /**
     * The controller of the current page
     *
     * @type {app.core.PageController}
     * @public
     */
    pageController: null,

    /**
     * Whether the controller is currently activating a view.
     *  * -1 = inactive
     *  *  0 = activating
     *  *  1 = active
     *
     * @type {int}
     * @public
     */
    pageActive: -1,

    /**
     * The media type of the media displayed on the current page.
     *
     * @type {string|null}
     * @public
     */
    pageMediaType: null,

    /**
     * The slug of the media displayed on the current page.
     *
     * @type {string|null}
     * @public
     */
    pageMediaSlug: null,

    /**
     * The id of the media displayed on the current page.
     *
     * @type {string|null}
     * @public
     */
    pageMediaId: null,

    /**
     * The slugs of the playlists displayed on the current page.
     *
     * @type {array}
     * @public
     */
    pagePlaylistSlugs: null,

    // Media Details State
    // --------------------------------

    /**
     * Reset media details state
     *
     * @public
     */
    resetMediaDetailsState: function() {
        this.set("mediaDetailsController", null);
        this.set("mediaDetailsActive", -1);
        this.set("mediaDetailsMediaType", null);
        this.set("mediaDetailsMediaSlug", null);
        this.set("mediaDetailsMediaId", null);
    },

    /**
     * The controller of the current Media Details Popup
     *
     * @type {app.core.PopupContentController}
     * @public
     */
    mediaDetailsController: null,

    /**
     * Whether the controller is currently activating a view.
     *  * -1 = inactive
     *  *  0 = activating
     *  *  1 = active
     *
     * @type {int}
     * @public
     */
    mediaDetailsActive: -1,

    /**
     * The media type of the media displayed in the Media Details Popup.
     *
     * @type {string|null}
     * @public
     */
    mediaDetailsMediaType: null,

    /**
     * The slug of the media displayed in the Media Details Popup.
     *
     * @type {string|null}
     * @public
     */
    mediaDetailsMediaSlug: null,

    /**
     * The id of the media displayed in the Media Details Popup.
     *
     * @type {string|null}
     * @public
     */
    mediaDetailsMediaId: null,

    // Media Player State
    // --------------------------------

    /**
     * Reset media player state
     *
     * @public
     */
    resetMediaPlayerState: function() {
        this.set("mediaPlayerController", null);
        this.set("mediaPlayerActive", -1);
        this.set("mediaPlayerMediaLanguage", null);
        this.set("mediaPlayerRootType", null);
        this.set("mediaPlayerRootSlug", null);
        this.set("mediaPlayerRootId", null);
        this.set("mediaPlayerMediaType", null);
        this.set("mediaPlayerMediaSlug", null);
        this.set("mediaPlayerMediaId", null);
        this.set("mediaPlayerArtistSlug", null);
        this.set("mediaPlayerPlaylistSlug", null);
        this.set("mediaPlayerCategorySlug", null);
    },

    /**
     * Reset media player view state
     *
     * @public
     */
    resetMediaPlayerViewState: function() {
        this.set("mediaPlayer", null);
        this.set("mediaPlayerReady", -1);
        this.set("mediaPlayerPlaying", false);
        this.set("mediaPlayerPlayingAds", false);
    },

    /**
     * The controller of the current media player
     *
     * @type {app.core.PageController}
     * @public
     */
    mediaPlayerController: null,

    /**
     * Whether the controller is currently activating a view.
     *  * -1 = inactive
     *  *  0 = activating
     *  *  1 = active
     *
     * @type {int}
     * @public
     */
    mediaPlayerActive: -1,

    /**
     * The current media player
     *
     * @type {app.MediaPlayer.AbstractAudioPlayer|app.MediaPlayer.AbstractImageViewer|app.MediaPlayer.AbstractReader|app.MediaPlayer.AbstractVideoPlayer|null}
     * @public
     */
    mediaPlayer: null,

    /**
     * Whether the media player is fully initialized and ready to be used.
     * Possible values:
     *  * -1 = no player
     *  *  0 = player initializing
     *  *  1 = player ready
     *
     * @type {int}
     * @public
     */
    mediaPlayerReady: -1,

    /**
     * Whether the media player is currently playing media.
     *
     * @type {boolean}
     * @public
     */
    mediaPlayerPlaying: false,

    /**
     * Whether the media player is currently playing ads.
     *
     * @type {boolean}
     * @public
     */
    mediaPlayerPlayingAds: false,

    /**
     * Current displayMode.
     *
     * @type {string|null}
     * @public
     */
    mediaPlayerDisplayMode: null,

    /**
     * The language of the current media
     *
     * @type {string|null}
     * @public
     */
    mediaPlayerMediaLanguage: null,

    /**
     * The media type of the root of the ancestry of the currently playing
     * media.
     *
     * @type {string|null}
     * @public
     */
    mediaPlayerRootType: null,

    /**
     * The slug of the root of the ancestry of the the currently playing media.
     *
     * @type {string|null}
     * @public
     */
    mediaPlayerRootSlug: null,

    /**
     * The id of the root of the ancestry of the the currently playing media.
     *
     * @type {string|null}
     * @public
     */
    mediaPlayerRootId: null,

    /**
     * The media type of the currently playing media.
     *
     * @type {string|null}
     * @public
     */
    mediaPlayerMediaType: null,

    /**
     * The slug of the currently playing media.
     *
     * @type {string|null}
     * @public
     */
    mediaPlayerMediaSlug: null,

    /**
     * The id of the currently playing media.
     *
     * @type {string|null}
     * @public
     */
    mediaPlayerMediaId: null,

    /**
     * The artist slug of the currently playing media.
     *
     * @type {string|null}
     * @public
     */
    mediaPlayerArtistSlug: null,

    /**
     * The playlist slug of the currently playing media.
     *
     * @type {string|null}
     * @public
     */
    mediaPlayerPlaylistSlug: null,

    /**
     * The category slug of the currently playing media.
     *
     * @type {string|null}
     * @public
     */
    mediaPlayerCategorySlug: null

});
